import { Link, useParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { articles, findArticle, relatedArticles } from "@/mocks/articles";

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = findArticle(slug ?? "");
  const fallback = articles[0];
  const current = article ?? fallback;
  const related = relatedArticles(current);

  usePageMeta(`${current.title} - 正版韩漫大全`, current.summary);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: current.title,
    description: current.summary,
    articleSection: current.category,
    datePublished: current.date,
    inLanguage: "zh-CN",
    publisher: { "@type": "Organization", name: "正版韩漫大全" },
  };

  return (
    <div className="bg-background-50">
      <div className="bg-primary-50/50">
        <div className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6 md:py-14">
          <nav className="text-xs text-foreground-500" aria-label="面包屑">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to="/articles" className="hover:text-primary-600">追更指南</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-800" aria-current="page">{current.category}</li>
            </ol>
          </nav>
          <span className="mt-5 inline-block rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
            {current.tag}
          </span>
          <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-900 md:text-3xl">
            {current.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="text-primary-600">{current.category}</span>
            <span>·</span>
            <span>{current.date}</span>
            <span>·</span>
            <span>阅读约 {current.readTime}</span>
          </div>
        </div>
      </div>

      <article className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6 md:py-14">
        <img
          src={current.cover}
          alt={`${current.title}封面`}
          className="aspect-[3/2] w-full rounded-lg object-cover object-top"
        />
        <div className="mt-8 space-y-6">
          {current.content.map((para, i) => (
            <p
              key={i}
              className={`text-[15px] leading-loose text-foreground-700 ${
                i === 0 ? "font-medium text-foreground-900" : ""
              }`}
            >
              {para}
            </p>
          ))}
        </div>
        <div className="mt-10 flex items-center gap-3 rounded-lg bg-accent-100 p-5">
          <i className="ri-quill-pen-line text-2xl text-accent-700"></i>
          <p className="text-sm text-accent-900">
            本文为正版韩漫大全原创内容，旨在帮助读者识别正版渠道、正确追更。转载请注明出处。
          </p>
        </div>
      </article>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-foreground-900 md:text-xl">相关推荐</h2>
            <Link to="/articles" className="whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700">
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
            {related.map((a) => (
              <Link
                key={a.id}
                to={`/article/${a.slug}`}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:border-primary-300"
              >
                <img
                  src={a.cover}
                  alt={`${a.title}封面`}
                  className="aspect-[3/2] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="p-4">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <span className="text-primary-600">{a.category}</span>
                    <span>·</span>
                    <span>{a.date}</span>
                  </div>
                  <h3 className="mt-2 text-sm font-semibold leading-snug text-foreground-900 group-hover:text-primary-600">
                    {a.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}